import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import { psidValidation } from "../../index";
import { Alert } from "components/UI/Alert/Alert";
import { Loader } from "components/UI/Loader/Loader";
import { Button } from "components/UI/Button/Button";
import { useInputChange } from "hooks/useInputChange";
import { Dialog, Transition } from "@headlessui/react";
import { useDispatch, useSelector } from "react-redux";
import { useCheckboxChange } from "hooks/useCheckboxChange";
import React, { Fragment, useEffect, useState } from "react";
import { InputText } from "components/UI/InputText/InputText";
import { getStoreList } from "model/dashboardStores/dStoresAPI";
import { ButtonLink } from "components/UI/ButtonLink/ButtonLink";
import { vatAllowedValue, vatList } from "constants/vatAllowedValue";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { InputCheckbox } from "components/UI/InputCheckbox/InputCheckbox";
import { InputNumberLimit } from "components/UI/InputNumberLimit/InputNumberLimit";
import { getNotDeletedCategoriesList } from "model/dashboardCategories/dCategoriesAPI";
import { ConfirmDialogDashboard } from "../../../DashboardContentTable/ConfirmDialogDashboard";
import {
  XIcon,
  TrashIcon,
  QrcodeIcon,
  ShoppingCartIcon,
} from "@heroicons/react/outline";
import {
  SelectBox,
  NULL_VALUE,
  typeValuesListItem,
} from "components/UI/SelectBox/SelectBox";
import {
  logoutSagaAC,
  addNotificationAC,
} from "model/system/systemActionCreators";
import {
  checkExistPSID,
  getProductByPsid,
  getProductItemById,
  searchProductByName,
  getProductByBarcode,
} from "model/dashboardProducts/dProductsAPI";
import {
  IdProductsState,
  IdProductsStateDataItem,
} from "model/dashboardProducts/dProductsReducer";
import {
  PRODUCT_OWNER,
  selectProductOwnerList,
} from "../../../../../../constants/productOwner";
import {
  dProductsAddItemSagaAC,
  dProductsRestoreDeletedItemWithNewDataSagaAC,
} from "model/dashboardProducts/dProductsActionCreators";
import {
  checkForbiddenChar,
  psidValidationEnv,
} from "components/containers/dashboard/dataProducts/constants";

type typeStoreItem = {
  id: string | number | null;
  name: string | React.ReactElement<TransProps>;
  price: number;
};

export type typePopupAdd = {
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export type typeAddProductsItemObject = {
  id: string | null;
  name: string;
  barcodes: string[] | [];
  psid: string;
  units: string;
  integerUnit: boolean;
  marked: boolean | null;
  category: {
    id: string | number;
  } | null;
  stores:
    | {
        store: {
          id: string | number | null;
        };
        price: number;
      }[]
    | [];
  vat: number | string;
  commissionTIN: string | null;
  commissionPINFL: string | null;
  active: boolean | null;
  unitCode: number | null;
  packageCode: string | null;
  productOwnerType: PRODUCT_OWNER;
};

export const PopupAdd: React.FC<typePopupAdd> = ({ setOpenPopup }) => {
  const dispatch = useDispatch();

  // System data
  const filters = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.filters,
  );
  const pagination = useSelector(
    (state: { dProducts: IdProductsState }) => state.dProducts.pagination,
  );

  // Did mount
  useEffect(() => {
    const setData = async () => {
      let responseCategories = await getNotDeletedCategoriesList();

      if (responseCategories.status === 200) {
        const newCategoriesList = [
          ...selectCategoryList,
          ...responseCategories.data,
        ];
        setSelectCategoryList(newCategoriesList);
      } else if (responseCategories.status === 401) {
        dispatch(logoutSagaAC());
      }

      let responseStores = await getStoreList({
        filters: { name: null, deleted: false },
        full: false,
      });

      if (responseStores.status === 200) {
        const newStoresList = [...selectStoreList, ...responseStores.data];
        setSelectStoreList(newStoresList);
      } else if (responseStores.status === 401) {
        dispatch(logoutSagaAC());
      }
    };

    setData().catch(console.error);
    // eslint-disable-next-line
  }, []);

  // Form data
  const { value: idValue, changeValueHandler: idChangeValueHandler } =
    useInputChange("");
  const { value: nameValue, changeValueHandler: nameChangeValueHandler } =
    useInputChange("");
  const {
    value: psidValue,
    setValue: setPsidValue,
    changeValueHandler: psidChangeValueHandler,
  } = useInputChange("");
  const { value: unitsValue, changeValueHandler: unitsChangeValueHandler } =
    useInputChange("");

  const [vatValue, setVatValue] = useState<number>(vatAllowedValue["0%"]);

  const {
    value: integerUnitValue,
    changeValueHandler: integerUnitCheckedValueHandler,
  } = useCheckboxChange(true);
  const {
    value: markedChecked,
    changeValueHandler: markedCheckedValueHandler,
  } = useCheckboxChange(false);
  const {
    value: commissionTINValue,
    setValue: setCommissionTINValue,
    changeValueHandler: commissionTINChangeValueHandler,
  } = useInputChange("");
  const {
    value: commissionPINFLValue,
    setValue: setCommissionPINFLValue,
    changeValueHandler: commissionPINFLChangeValueHandler,
  } = useInputChange("");
  const { value: activeValue, changeValueHandler: activeCheckedValueHandler } =
    useCheckboxChange(true);
  const { value: unitCodeValue, setValue: setUnitCodeValue } =
    useInputChange("");
  const { value: packageCodeValue, setValue: setPackageCodeValue } =
    useInputChange("");

  const [categoryIdValue, setCategoryIdValue] = useState<
    string | number | null
  >(null);
  const [selectCategoryList, setSelectCategoryList] = useState<
    typeValuesListItem[]
  >([]);

  const { value: priceValue, setValue: setPriceValue } = useInputChange("");

  const [storeIdValue, setStoreIdValue] = useState<string | number | null>(
    null,
  );
  const [storeListValue, setStoreListValue] = useState<typeStoreItem[]>([]);
  const [selectStoreList, setSelectStoreList] = useState<typeValuesListItem[]>(
    [],
  );
  const [packageCodeList, setPackageCodeList] = useState<string[]>([]);
  const [packageCodeModal, togglePackageCodeModal] = useState<boolean>(false);

  // const [markedChecked, setMarkedChecked] = useState<boolean>(false);

  const {
    value: barcodeValue,
    changeValueHandler: barcodeChangeValueHandler,
    setValue: setBarcodeValue,
  } = useInputChange("");
  const [barcodeList, setBarcodeList] = useState<string[]>([]);

  const [productOwnerValue, setProductOwnerValue] =
    useState<PRODUCT_OWNER | null>(null);

  const [loader, setLoader] = useState(false);

  const [, setForbiddenChar] = useState<boolean>(false);

  const [isOpenRestorePopup, setIsOpenRestorePopup] = useState(false);
  const [deletedProductData, setDeletedProductData] =
    useState<IdProductsStateDataItem | null>(null);

  const [isTouched, setIsTouched] = useState({
    psidTouched: false,
    nameTouched: false,
    unitsTouched: false,
    productOwnerTouched: false,
  });

  // Validation
  const [validationObj, setValidationObj] = useState({
    uniqueId: true,
    requiredName: true,
    isForbiddenChar: false,
    requiredVat: true,
    uniqueName: true,
    requiredUnits: true,
    validBarcode: true,
    requiredPSID: true,
    validPsid: true,
    existPsid: true,
    isRequiredBarcode: false,
    validChooseTinPinfl: true,
    validTin: true,
    validPinfl: true,
    requiredProductOwner: true,
  });

  useEffect(() => {
    if (unitsValue.trim() !== "" && !isTouched.unitsTouched) {
      setIsTouched((prev) => ({ ...prev, unitsTouched: true }));
    }

    if (isTouched.unitsTouched) {
      if (unitsValue.trim() === "") {
        setValidationObj((v) => ({
          ...v,
          requiredUnits: false,
        }));
      } else {
        setValidationObj((v) => ({
          ...v,
          requiredUnits: true,
        }));
      }
    }
  }, [unitsValue, isTouched]);

  useEffect(() => {
    if (typeof vatValue === "string" || vatValue === null) {
      setValidationObj((v) => {
        return { ...v, ...{ requiredVat: false } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ requiredVat: true } };
      });
    }
  }, [vatValue]);

  useEffect(() => {
    if (priceValue.length > 2) {
      setPriceValue(priceValue.match(/\d{0,12}.\d{0,2}/)![0]);
    }
    if (+priceValue < 0) {
      setPriceValue(Math.abs(+priceValue).toString());
    }

    // eslint-disable-next-line
  }, [priceValue]);

  React.useEffect(() => {
    if (deletedProductData) {
      setDeletedProductData(null);
    }

    // eslint-disable-next-line
  }, [nameValue]);

  useEffect(() => {
    if (nameValue.trim() !== "" && !isTouched.nameTouched) {
      setIsTouched((prev) => ({ ...prev, nameTouched: true }));
      setValidationObj((v) => ({
        ...v,
        requiredName: true,
        uniqueName: true,
        isForbiddenChar: true,
      }));
    }

    if (isTouched.nameTouched) {
      if (nameValue.trim() === "") {
        setForbiddenChar(false);
        setValidationObj((v) => ({
          ...v,
          requiredName: false,
          uniqueName: true,
          isForbiddenChar: false,
        }));
      } else {
        const forbiddenChar = checkForbiddenChar(nameValue.trim());
        setForbiddenChar(!forbiddenChar);
        setValidationObj((v) => ({
          ...v,
          requiredName: true,
          uniqueName: true,
          isForbiddenChar: !forbiddenChar,
        }));
      }
    }
  }, [nameValue, deletedProductData, isTouched]);

  useEffect(() => {
    setValidationObj((v) => {
      return { ...v, ...{ uniqueId: true } };
    });
  }, [idValue]);

  useEffect(() => {
    setValidationObj((v) => {
      return { ...v, ...{ validBarcode: true } };
    });
  }, [barcodeValue]);

  useEffect(() => {
    if (markedChecked && barcodeList.length === 0) {
      setValidationObj((v) => {
        return { ...v, ...{ isRequiredBarcode: true } };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, ...{ isRequiredBarcode: false } };
      });
    }
  }, [markedChecked, barcodeList]);

  useEffect(() => {
    if (psidValue.trim() !== "") {
      setIsTouched({ ...isTouched, psidTouched: true });
    }

    if (psidValidationEnv && isTouched.psidTouched) {
      if (psidValue.trim() !== "") {
        if (psidValue.match(/\D+/)) {
          setPsidValue(psidValue.replace(/\D/gi, ""));
        }
        if (psidValidation(psidValue)) {
          setValidationObj((v) => {
            return {
              ...v,
              ...{
                validPsid: true,
                requiredPSID: true,
                existPsid: true,
              },
            };
          });
        } else {
          setValidationObj((v) => {
            return {
              ...v,
              ...{
                validPsid: false,
                requiredPSID: true,
                existPsid: true,
              },
            };
          });
        }
      } else {
        setValidationObj((v) => {
          return {
            ...v,
            ...{
              validPsid: false,
              requiredPSID: false,
              existPsid: true,
            },
          };
        });
      }
    }

    // eslint-disable-next-line
  }, [psidValue]);

  useEffect(() => {
    if (unitCodeValue.trim().startsWith("0")) {
      setUnitCodeValue(unitCodeValue.trim().substring(1));
    }
    // eslint-disable-next-line
  }, [unitCodeValue]);

  useEffect(() => {
    if (packageCodeValue.trim().startsWith("0")) {
      setPackageCodeValue(packageCodeValue.trim().substring(1));
    }

    // eslint-disable-next-line
  }, [packageCodeValue]);

  useEffect(() => {
    if (commissionTINValue.trim() !== "") {
      const tin = commissionTINValue.trim();
      if (tin.startsWith("0")) {
        setCommissionTINValue(tin.substring(1).replace(/\D/, ""));
      } else {
        setCommissionTINValue(tin.replace(/\D/, ""));
      }
    }
    if (commissionPINFLValue.trim() !== "") {
      const pinfl = commissionPINFLValue.trim();
      if (pinfl.startsWith("0")) {
        setCommissionPINFLValue(pinfl.substring(1).replace(/\D/, ""));
      } else {
        setCommissionPINFLValue(pinfl.replace(/\D/, ""));
      }
    }
    if (
      commissionTINValue.trim() !== "" &&
      commissionPINFLValue.trim() !== ""
    ) {
      setValidationObj((v) => {
        return { ...v, validChooseTinPinfl: false };
      });
    } else {
      setValidationObj((v) => {
        return { ...v, validChooseTinPinfl: true };
      });
    }

    // eslint-disable-next-line
  }, [commissionTINValue, commissionPINFLValue]);

  useEffect(() => {
    if (commissionTINValue.trim() !== "") {
      if (commissionTINValue.trim().length !== 9) {
        setValidationObj((v) => {
          return { ...v, validTin: false };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, validTin: true };
        });
      }
    } else {
      setValidationObj((v) => {
        return { ...v, validTin: true };
      });
    }
  }, [commissionTINValue]);

  useEffect(() => {
    if (commissionPINFLValue.trim() !== "") {
      if (commissionPINFLValue.trim().length !== 14) {
        setValidationObj((v) => {
          return { ...v, validPinfl: false };
        });
      } else {
        setValidationObj((v) => {
          return { ...v, validPinfl: true };
        });
      }
    } else {
      setValidationObj((v) => {
        return { ...v, validPinfl: true };
      });
    }
  }, [commissionPINFLValue]);

  useEffect(() => {
    if (
      productOwnerValue !== NULL_VALUE.id &&
      productOwnerValue !== null &&
      !isTouched.productOwnerTouched
    ) {
      setIsTouched({ ...isTouched, productOwnerTouched: true });
    }

    if (isTouched.productOwnerTouched) {
      if (productOwnerValue !== NULL_VALUE.id && productOwnerValue !== null) {
        setValidationObj((v) => ({
          ...v,
          requiredProductOwner: true,
        }));
      } else {
        setValidationObj((v) => ({
          ...v,
          requiredProductOwner: false,
        }));
      }
    }
  }, [productOwnerValue, isTouched]);

  // Add prise
  const onAddPriceHandler = () => {
    const storeObj = selectStoreList.find((item) => item.id === storeIdValue);
    const storeItem: typeStoreItem = {
      id: storeIdValue,
      name: storeObj ? storeObj.name : "",
      price: parseFloat(Math.abs(+priceValue).toString()),
    };

    const changeItemIndex = storeListValue.findIndex(
      (item) => item.id === storeIdValue,
    );
    if (changeItemIndex >= 0) {
      const newList = storeListValue;
      newList[changeItemIndex] = storeItem;
      setStoreListValue(newList);
    } else {
      const newList = storeListValue;
      newList.push(storeItem);
      setStoreListValue(newList);
    }

    setPriceValue("");
  };

  // Delete prise
  const onDeletePriceHandler = (id: string | number | null) => {
    const newStoreListValue = storeListValue.filter((item) => item.id !== id);
    setStoreListValue(newStoreListValue);
  };

  // Add barcode
  const onAddBarcodeHandler = async () => {
    if (barcodeValue.trim() !== "") {
      const newList: string[] = [...barcodeList];
      const listItem = newList?.find((item) => item === barcodeValue);

      if (listItem === undefined) {
        newList.push(barcodeValue.trim());
        setBarcodeList(newList);
        setBarcodeValue("");

        autoFill(
          () => getProductByBarcode(barcodeValue.trim()),
          "barcode",
        ).catch(console.error);
      } else {
        setValidationObj((v) => {
          return { ...v, ...{ validBarcode: false } };
        });
      }
    }
  };

  // Add barcode by Enter
  const onEnterBarcode = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      await onAddBarcodeHandler();
    }
  };

  // Delete barcode
  const onDeleteBarcodeHandler = (barcode: string) => {
    const newList: string[] = [...[], ...barcodeList];
    const listItemIndex = newList.findIndex((item) => item === barcode);
    if (listItemIndex >= 0) newList.splice(listItemIndex, 1);
    setBarcodeList(newList);
  };

  // Add product
  const onAddHandler = async (e: React.FormEvent) => {
    e.preventDefault();

    // Unique validation helper
    const uniqueValidate = (response: any) => {
      let validationValue = false;

      if (response.status === 200) {
        validationValue = false;
      } else {
        if (response.status === 404) {
          validationValue = true;
        } else if (response.status === 401) {
          dispatch(logoutSagaAC());
        } else {
          const id = new Date();
          dispatch(
            addNotificationAC({
              id: id.getTime().toString(),
              message: "ERROR_SERVER_CONNECTION",
            }),
          );
        }
      }
      return validationValue;
    };

    setLoader(true);

    // Validation Id
    let validId: boolean;
    if (idValue) {
      let responseId = await getProductItemById({ itemId: idValue.trim() });

      validId = !responseId.data;
    } else {
      validId = true;
    }

    // Validation Name
    let validName = false;
    let responseName = await searchProductByName({
      filters: { name: nameValue.trim() },
    });

    // hotfix comments
    // if (responseName.status === 200) {
    //   if (responseName.data?.content?.length === 0) {
    //     validName = true;
    //   } else {
    //     if (
    //       responseName.data.content[0].name.trim() === nameValue.trim() &&
    //       responseName.data.content[0].deleted === true
    //     ) {
    //       setDeletedProductData(responseName.data.content[0]);
    //       setIsOpenRestorePopup(true);
    //     } else {
    //       validName = false;
    //     }
    //   }
    // } else {
    //   if (responseName.status === 404) {
    //     validName = true;
    //   } else if (responseName.status === 401) {
    //     dispatch(logoutSagaAC());
    //   } else {
    //     const id = new Date();
    //     dispatch(
    //       addNotificationAC({
    //         id: id.getTime().toString(),
    //         message: "ERROR_SERVER_CONNECTION",
    //       }),
    //     );
    //   }
    // }

    // hotfix change url
    if (responseName.status === 200) {
      if (responseName.data?.length === 0) {
        validName = true;
      } else {
        if (
          responseName.data[0].name.trim() === nameValue.trim() &&
          responseName.data[0].deleted === true
        ) {
          setDeletedProductData(responseName.data[0]);
          setIsOpenRestorePopup(true);
        } else {
          validName = false;
        }
      }
    } else {
      if (responseName.status === 404) {
        validName = true;
      } else if (responseName.status === 401) {
        dispatch(logoutSagaAC());
      } else {
        const id = new Date();
        dispatch(
          addNotificationAC({
            id: id.getTime().toString(),
            message: "ERROR_SERVER_CONNECTION",
          }),
        );
      }
    }

    // validation exist PSID
    let existPsid: boolean = true;
    if (psidValidationEnv) {
      let responsePsid = await checkExistPSID({ psid: psidValue.trim() });
      existPsid = !uniqueValidate(responsePsid);
    }

    setValidationObj({
      ...validationObj,
      ...{ uniqueId: validId, uniqueName: validName, existPsid: existPsid },
    });

    setLoader(false);

    if (validId && validName && existPsid && productOwnerValue) {
      const dataAddObject: typeAddProductsItemObject = {
        id: !!idValue.trim() ? idValue.trim() : null,
        name: nameValue.trim(),
        barcodes: barcodeList,
        psid: psidValue.trim(),
        units: unitsValue.trim(),
        integerUnit: integerUnitValue,
        marked: markedChecked,
        category: categoryIdValue ? { id: categoryIdValue } : null,
        stores: storeListValue.map((item) => ({
          store: { id: item.id },
          price: item.price,
        })),
        vat: vatValue,
        commissionTIN: !!commissionTINValue.trim()
          ? commissionTINValue.trim()
          : null,
        commissionPINFL: !!commissionPINFLValue.trim()
          ? commissionPINFLValue.trim()
          : null,
        active: activeValue,
        unitCode: !!unitCodeValue.trim() ? +unitCodeValue.trim() : 0,
        packageCode: !!packageCodeValue.trim() ? packageCodeValue.trim() : null,
        productOwnerType: productOwnerValue,
      };
      dispatch(
        dProductsAddItemSagaAC(dataAddObject, filters, {
          page: pagination.pageNumber,
          size: pagination.itemsPerPage!,
        }),
      );
      setOpenPopup(false);
    }
  };

  const onRestoreHandler = async () => {
    // Unique validation helper
    const uniqueValidate = (response: any) => {
      let validationValue = false;

      if (response.status === 200) {
        validationValue = false;
      } else {
        if (response.status === 404) {
          validationValue = true;
        } else if (response.status === 401) {
          dispatch(logoutSagaAC());
        } else {
          const id = new Date();
          dispatch(
            addNotificationAC({
              id: id.getTime().toString(),
              message: "ERROR_SERVER_CONNECTION",
            }),
          );
        }
      }
      return validationValue;
    };

    setLoader(true);

    // validation exist PSID
    let existPsid: boolean;
    let responsePsid = await checkExistPSID({ psid: psidValue.trim() });
    existPsid = !uniqueValidate(responsePsid);

    setValidationObj({ ...validationObj, existPsid: existPsid });
    setLoader(false);

    if (!existPsid) {
      setIsOpenRestorePopup(false);
      return;
    }

    if (deletedProductData !== null && existPsid && productOwnerValue) {
      const data = {
        id: deletedProductData.id,
        name: nameValue.trim(),
        barcodes: barcodeList,
        psid: psidValue.trim(),
        units: unitsValue.trim(),
        unitMeasurement: unitsValue.trim(),
        integerUnit: integerUnitValue,
        marked: markedChecked,
        category: categoryIdValue ? { id: categoryIdValue } : null,
        stores: storeListValue.map((item) => ({
          store: { id: item.id },
          price: item.price,
        })),
        vat: vatValue,
        commissionTIN: !!commissionTINValue.trim()
          ? commissionTINValue.trim()
          : null,
        commissionPINFL: !!commissionPINFLValue.trim()
          ? commissionPINFLValue.trim()
          : null,
        active: activeValue,
        unitCode: !!unitCodeValue.trim() ? +unitCodeValue.trim() : null,
        packageCode: !!packageCodeValue.trim() ? packageCodeValue.trim() : null,
        productOwnerType: productOwnerValue,
      };
      const response = dispatch(
        dProductsRestoreDeletedItemWithNewDataSagaAC(data, filters, {
          page: pagination.pageNumber,
          size: pagination.itemsPerPage!,
        }),
      );
      setIsOpenRestorePopup(false);
      if (!!response) {
        setOpenPopup(false);
      }
    }
  };

  const onCancelRestoreHandler = () => {
    setLoader(false);
    setIsOpenRestorePopup(false);
    // setDeletedProductData(null)
  };

  const isDisabled =
    !(validationObj.requiredName && validationObj.requiredUnits) ||
    !validationObj.requiredVat ||
    +vatValue < 0 ||
    +vatValue > 100 ||
    isNaN(+vatValue) ||
    !validationObj.validPsid ||
    !validationObj.requiredPSID ||
    validationObj.isRequiredBarcode ||
    !validationObj.validChooseTinPinfl ||
    !validationObj.existPsid ||
    !validationObj.validPinfl ||
    !validationObj.validTin ||
    validationObj.isForbiddenChar ||
    !validationObj.requiredProductOwner ||
    !isTouched.psidTouched ||
    !isTouched.productOwnerTouched ||
    !isTouched.unitsTouched ||
    !isTouched.nameTouched ||
    loader;

  const autoFill = async (
    fetcher: () => Promise<any>,
    input: "psid" | "barcode",
  ) => {
    try {
      if (input === "barcode") {
        const res = await fetcher();

        if (Array.isArray(res.data)) {
          const barcodes = res.data.reduce(
            (acc: any, current: any) => {
              if (current.barcode) {
                acc.barcodes[current.barcode] = current.barcode;
              }

              if (current.packageCode) {
                acc.packageCodes[current.packageCode] = current.packageCode;
              }

              return acc;
            },
            { barcodes: {}, packageCodes: {} },
          );

          // if (res.data[0]) {
          //   setPsidValue(res.data[0].psid);
          // } else {
          //   setPsidValue("");
          // }

          const barcodesList = Object.keys(barcodes.barcodes);
          if (barcodesList.length) {
            // setBarcodeList(barcodesList);
          } else {
            // setBarcodeList([]);
          }

          const packageCodesList = Object.keys(barcodes.packageCodes);
          if (packageCodesList.length) {
            setPackageCodeList(packageCodesList);

            togglePackageCodeModal(true);
          }
        }
      } else {
        const res = await fetcher();

        if (Array.isArray(res.data)) {
          const barcodes = res.data.reduce(
            (acc: any, current: any) => {
              if (current.barcode) {
                acc.barcodes[current.barcode] = current.barcode;
              }

              if (current.packageCode) {
                acc.packageCodes[current.packageCode] = current.packageCode;
              }

              // if (current.label) {
              //   acc.label = current.label;
              // }

              return acc;
            },
            { barcodes: {}, packageCodes: {} },
          );

          const barcodesList = Object.keys(barcodes.barcodes);
          if (barcodesList.length) {
            setBarcodeList(barcodesList);
          } else {
            setBarcodeList([]);
          }

          const packageCodesList = Object.keys(barcodes.packageCodes);
          if (packageCodesList.length) {
            setPackageCodeList(packageCodesList);

            togglePackageCodeModal(true);
          }

          // setMarkedChecked(!!barcodes.label)
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <form onSubmit={(e) => onAddHandler(e)}>
      <div className="relative">
        <div className="relative grid gap-4 xl:grid-cols-3">
          <div>
            <InputText
              id="id"
              name="id"
              value={idValue}
              label={<Trans>Product ID</Trans>}
              errorMessage={
                validationObj.uniqueId ? undefined : (
                  <Trans>This id already exists</Trans>
                )
              }
              onChange={idChangeValueHandler}
              labelClassName="text-left"
              maxLength={150}
            />
          </div>

          <div>
            {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
              <InputText
                id="psid"
                name="psid"
                value={psidValue}
                labelClassName="text-left"
                required={psidValidationEnv}
                onChange={(e) => {
                  psidChangeValueHandler(e);
                  // setMarkedChecked(false)
                  if (psidValidation(e.target.value)) {
                    autoFill(
                      () => getProductByPsid(e.target.value),
                      "psid",
                    ).catch(console.error);
                  }
                }}
                maxLength={psidValidationEnv ? 17 : 30}
                label={
                  psidValidationEnv ? (
                    <Trans>
                      PSID{" "}
                      <span className="text-gray-500 font-light italic text-xxs">
                        Check the psid on{" "}
                        <a
                          target="_blank"
                          rel="noreferrer"
                          href="https://tasnif.soliq.uz"
                          className="font-medium underline"
                        >
                          tasnif.soliq.uz
                        </a>
                      </span>
                    </Trans>
                  ) : (
                    <Trans>PSID</Trans>
                  )
                }
                errorMessage={
                  validationObj.requiredPSID ? (
                    validationObj.validPsid ? (
                      validationObj.existPsid ? undefined : (
                        <Trans>
                          The psid doesn't exist in the tax database.
                        </Trans>
                      )
                    ) : (
                      <Trans>It consists from 17 digits</Trans>
                    )
                  ) : (
                    <Trans>Required field</Trans>
                  )
                }
              />
            )}
          </div>

          <div>
            <SelectBox
              label={<Trans>Category</Trans>}
              valuesList={selectCategoryList}
              valueId={categoryIdValue}
              setIdValue={setCategoryIdValue}
              labelClassname="text-left"
            />
          </div>

          <div
            className={
              process.env.REACT_APP_CUSTOMER === "SKAPPSK"
                ? "sm:col-span-2"
                : ""
            }
          >
            <InputText
              id="name"
              name="name"
              value={nameValue}
              label={<Trans>Product name</Trans>}
              onChange={nameChangeValueHandler}
              errorMessage={
                validationObj.requiredName ? (
                  validationObj.uniqueName ? (
                    validationObj.isForbiddenChar ? (
                      <Trans>
                        The name consists only of prohibited characters. Edit
                        the name.
                      </Trans>
                    ) : undefined
                  ) : (
                    <Trans>This name already exists</Trans>
                  )
                ) : (
                  <Trans>Required field</Trans>
                )
              }
              labelClassName="text-left"
              required={true}
              maxLength={150}
            />
            {deletedProductData && (
              <p className="text-xs mt-1 text-red-500 ">
                <span className={"mr-2"}>
                  <Trans>Product with this name was deleted.</Trans>
                </span>
                <ButtonLink
                  label={<Trans>Restore ?</Trans>}
                  type={"button"}
                  onClick={() => setIsOpenRestorePopup(true)}
                />
              </p>
            )}
          </div>

          <div>
            <InputText
              id="units"
              name="units"
              value={unitsValue}
              label={<Trans>Units</Trans>}
              errorMessage={
                validationObj.requiredUnits ? undefined : (
                  <Trans>Required field</Trans>
                )
              }
              onChange={unitsChangeValueHandler}
              labelClassName="text-left"
              required={true}
              maxLength={30}
            />
          </div>

          <div className="flex flex-col xl:ml-12">
            <label className="block mb-1 text-sm font-medium text-gray-700 text-left">
              <Trans>Sell by piece</Trans>
            </label>
            <div className="flex items-center flex-grow">
              <InputCheckbox
                id="integerUnitValue"
                name="integerUnitValue"
                label={<Trans>By piece</Trans>}
                checked={integerUnitValue}
                onChange={integerUnitCheckedValueHandler}
              />
            </div>
          </div>

          <div>
            <InputNumberLimit
              id="unitCode"
              name="unitCode"
              value={unitCodeValue}
              setValue={setUnitCodeValue}
              label={<Trans>Unit code</Trans>}
              // onChange={ unitCodeChangeValueHandler }
              labelClassName="text-left"
              digitBeforeDot={3}
              placeholder={"1 - 999"}
            />
          </div>

          <div>
            <InputNumberLimit
              id="packageCode"
              name="packageCode"
              value={packageCodeValue}
              setValue={setPackageCodeValue}
              label={<Trans>Package code</Trans>}
              // onChange={ packageCodeChangeValueHandler }
              labelClassName="text-left"
              digitBeforeDot={7}
              placeholder={"1 - 9999999"}
            />
          </div>

          <div className="flex flex-col xl:ml-12">
            <label className="block mb-1 text-sm font-medium text-gray-700 text-left">
              <Trans>Active</Trans>
            </label>
            <div className="flex items-center flex-grow">
              <InputCheckbox
                id="activeValue"
                name="activeValue"
                label={<Trans>Active</Trans>}
                checked={activeValue}
                onChange={activeCheckedValueHandler}
              />
            </div>
          </div>

          <div className="flex flex-col">
            <div className="flex-grow">
              <SelectBox
                label={<Trans>Product/service owner type</Trans>}
                valuesList={selectProductOwnerList}
                required={true}
                valueId={productOwnerValue}
                setIdValue={setProductOwnerValue}
                labelClassname="text-left"
                errorMessage={
                  validationObj.requiredProductOwner ? undefined : (
                    <Trans>Required field</Trans>
                  )
                }
              />
            </div>
          </div>

          <div
            className={
              process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? "hidden" : ""
            }
          >
            <InputText
              id="commissionTIN"
              name="commissionTIN"
              value={commissionTINValue}
              label={
                <Trans>
                  Commission TIN{" "}
                  <span className={"text-gray-500 font-light italic text-xxs"}>
                    9 digits
                  </span>
                </Trans>
              }
              errorMessage={
                validationObj.validChooseTinPinfl ? (
                  validationObj.validTin ? undefined : (
                    <Trans>
                      Enter a valid value: 9 digits, leading 0 is not allowed{" "}
                    </Trans>
                  )
                ) : (
                  <Trans>Choose only one, TIN or PINFL</Trans>
                )
              }
              onChange={commissionTINChangeValueHandler}
              labelClassName="text-left"
              maxLength={9}
            />
          </div>
          <div
            className={
              process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? "hidden" : ""
            }
          >
            <InputText
              id="commissionPINFL"
              name="commissionPINFL"
              value={commissionPINFLValue}
              label={
                <Trans>
                  Commission PINFL{" "}
                  <span className={"text-gray-500 font-light italic text-xxs"}>
                    14 digits
                  </span>
                </Trans>
              }
              errorMessage={
                validationObj.validChooseTinPinfl ? (
                  validationObj.validPinfl ? undefined : (
                    <Trans>
                      Enter a valid value : 14 digits, leading 0 is not allowed
                    </Trans>
                  )
                ) : (
                  <Trans>Choose only one, TIN or PINFL</Trans>
                )
              }
              onChange={commissionPINFLChangeValueHandler}
              labelClassName="text-left"
              maxLength={14}
            />
          </div>

          <div
            className={
              process.env.REACT_APP_CUSTOMER === "SKAPPSK" ? "hidden" : ""
            }
          >
            <SelectBox
              id="vat"
              withoutEmpty
              label={<Trans>VAT in %</Trans>}
              valuesList={vatList}
              required={true}
              valueId={vatValue}
              setIdValue={setVatValue}
              labelClassname="text-left"
              errorMessage={
                validationObj.requiredVat ? undefined : (
                  <Trans>Required field</Trans>
                )
              }
            />
          </div>
          <div
            className={
              validationObj.isRequiredBarcode ? "opacity-100" : "opacity-0"
            }
          >
            <Alert
              type={"info"}
              title={
                <Trans>
                  Check for barcode!
                  <br /> Barcode required for marked product. <br />
                  Marked goods can only be piece.
                </Trans>
              }
            />
          </div>

          <div className="flex flex-col xl:ml-12">
            <label className="block mb-1 text-sm font-medium text-gray-700 text-left">
              <Trans>Marked product</Trans>
            </label>
            {/*<div className="flex items-center flex-grow pointer-events-none">*/}
            <div className="flex items-center flex-grow">
              <InputCheckbox
                id="marked"
                name="marked"
                label={<Trans>Marked</Trans>}
                checked={markedChecked}
                onChange={markedCheckedValueHandler}
                // readOnly
              />
            </div>
          </div>
        </div>

        <div className="grid gap-4">
          <div className="mt-8 flex flex-col gap-2">
            <span className="flex gap-x-1 font-medium">
              <span className="text-gray-400">
                <ShoppingCartIcon className="h-6 w-6" />
              </span>
              <Trans>Stores</Trans>
            </span>

            <div className="border-t pt-2 flex flex-wrap gap-2 items-end">
              <div className="flex-grow">
                <SelectBox
                  label={<Trans>Store</Trans>}
                  valuesList={selectStoreList}
                  valueId={storeIdValue}
                  setIdValue={setStoreIdValue}
                  labelClassname="text-left"
                />
              </div>

              <InputNumberLimit
                id="price"
                label={<Trans>Price</Trans>}
                placeholder="0.00"
                value={priceValue}
                setValue={setPriceValue}
                digitBeforeDot={11}
                digitsAfterDot={2}
              />

              <div>
                <Button
                  secondary={true}
                  disabled={!(storeIdValue && priceValue)}
                  type="button"
                  label={<Trans>Set price</Trans>}
                  onClick={onAddPriceHandler}
                />
              </div>
            </div>

            <div className="overflow-hidden appearance-none mt-2 block w-full border border-gray-300 rounded-md text-gray-600 sm:text-sm">
              {storeListValue && storeListValue.length ? (
                <>
                  <div className="flex flex-col divide-y">
                    {storeListValue.map((item, index) => (
                      <div
                        key={item.id + index.toString()}
                        className={`flex gap-x-6 items-center divided-y ${
                          index % 2 !== 0 ? "bg-gray-50 " : " "
                        }`}
                      >
                        <span className="flex-grow text-left pl-5 py-2 text-md">
                          {item.name}
                        </span>
                        <span className="flex-grow text-right py-2 text-md">
                          {numberCurrencyFormat(+item.price.toFixed(2))}
                        </span>
                        <span>
                          <div
                            className="p-3 border-l w-15 cursor-pointer"
                            onClick={() => onDeletePriceHandler(item.id)}
                          >
                            <TrashIcon className="h-5 w-5 " />
                          </div>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <span className="block px-3 py-2 text-gray-400">
                  <Trans>This product is not sold in any store.</Trans>
                </span>
              )}
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-2">
            <span className="flex gap-x-1 font-medium">
              <span className="text-gray-400">
                <QrcodeIcon className="h-6 w-6" />
              </span>
              <Trans>Barcodes</Trans>
            </span>

            <div className="border-t pt-2 flex flex-wrap gap-2 items-end">
              <div className="flex-grow">
                <InputText
                  id="barcode"
                  label={<Trans>Barcode</Trans>}
                  value={barcodeValue}
                  onChange={barcodeChangeValueHandler}
                  onKeyDown={(e) => onEnterBarcode(e)}
                  maxLength={14}
                  errorMessage={
                    validationObj.isRequiredBarcode ? (
                      <Trans>Required field</Trans>
                    ) : undefined
                  }
                />
              </div>
              <div
                className={`${
                  validationObj.isRequiredBarcode ? "self-center" : ""
                }`}
              >
                <Button
                  secondary={true}
                  disabled={!barcodeValue}
                  type="button"
                  label={<Trans>Set barcode</Trans>}
                  onClick={onAddBarcodeHandler}
                />
              </div>
            </div>
            <p
              className={`text-xs text-red-500 ${
                validationObj.validBarcode ? "opacity-0" : "opacity-100"
              }`}
            >
              <Trans>This barcode already exists</Trans>
            </p>

            <div className="overflow-hidden appearance-none mt-1 block w-full border border-gray-300 rounded-md text-gray-600 sm:text-sm">
              {barcodeList && barcodeList.length ? (
                <>
                  <div className="flex flex-wrap gap-x-4 px-4 py-3">
                    {barcodeList.map((item, index) => (
                      <div
                        key={item + index}
                        className="flex gap-x-1 items-center"
                      >
                        <span className="flex-grow text-left text-md">
                          {item}
                        </span>
                        <span>
                          <div
                            className="cursor-pointer"
                            onClick={() => onDeleteBarcodeHandler(item)}
                          >
                            <XIcon className="h-5 w-5 " />
                          </div>
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <span className="block text-gray-400 px-3 py-2">
                  <Trans>This product have no any barcodes.</Trans>
                </span>
              )}
            </div>
          </div>
        </div>

        {loader ? <Loader /> : null}
      </div>

      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <Button
          disabled={isDisabled}
          type="submit"
          label={<Trans>Add</Trans>}
        />
        <Button
          type="button"
          secondary={true}
          label={<Trans>Cancel</Trans>}
          onClick={() => setOpenPopup(false)}
        />
        <Alert
          className={"flex-grow"}
          type={"info"}
          title={
            <Trans>
              Fields marked with "<b className="text-red-500">*</b>" are
              required
            </Trans>
          }
        />
      </div>

      <ConfirmDialogDashboard
        notClose={true}
        type="info"
        title={
          <Trans>
            Product with name <b>{nameValue.trim()}</b> already exists,
            <br /> but was deleted.
          </Trans>
        }
        message={
          validationObj.existPsid ? (
            <Trans>
              Restore the product <br /> by replacing the old data with the new
              one?
            </Trans>
          ) : (
            <Trans>
              Check PSID. Product can't be restored with invalid PSID
            </Trans>
          )
        }
        primaryButton={
          validationObj.existPsid
            ? {
                title: <Trans>Restore</Trans>,
                action: () => onRestoreHandler(),
              }
            : undefined
        }
        secondaryButton={{
          title: <Trans>Cancel</Trans>,
          action: () => onCancelRestoreHandler(),
        }}
        openDialog={isOpenRestorePopup}
        setOpenDialog={setIsOpenRestorePopup}
      />

      <Transition.Root show={packageCodeModal} as={Fragment}>
        <Dialog
          static
          as="div"
          open={true}
          onClose={() => {
            togglePackageCodeModal(false);
            setPackageCodeList([]);
          }}
          className="fixed z-20 inset-0 overflow-y-auto"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              leaveTo="opacity-0"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leaveFrom="opacity-100"
              leave="ease-in duration-200"
              enter="ease-out duration-300"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>
            <span
              aria-hidden="true"
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              leave="ease-in duration-200"
              enter="ease-out duration-300"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    onClick={() => {
                      togglePackageCodeModal(false);
                      setPackageCodeList([]);
                    }}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex flex-col">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4 print:hidden">
                    <Trans>Select a package code</Trans>
                  </h3>
                  <div className="flex items-center justify-center">
                    <div className="h-48 overflow-y-auto bg-white py-1 border rounded-md w-full">
                      {packageCodeList.map((item) => (
                        <p
                          key={item}
                          className="bg-white p-2 hover:text-white hover:bg-primary-600 cursor-pointer"
                          onClick={() => {
                            setPackageCodeValue(item);
                            togglePackageCodeModal(false);

                            setPackageCodeList([]);
                          }}
                        >
                          {item}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </form>
  );
};
